import { Tab } from '@headlessui/react';
import cn from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo, useState } from 'react';

import { Icon, Spinner } from 'components';
import ActionMenu from 'components/ActionMenu';
import { ButtonV3 } from 'components/ComponentV2';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import { Button } from 'components/form';
import { Row } from 'components/layout';
import {
  ConfirmationScreen,
  NewsletterColumns,
  SocietyTabsType,
  useNewsletters,
} from 'hooks/useNewsletters.hook';
import { useNotification } from 'hooks/useNotification';

import { useSocietyProfile } from 'pages/society-profile/UseSocietyProfile.hook';
import { NewsletterAttributes } from 'lib/models/newsletter';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './SocietyNewslettersTable.module.scss';

export const EventStateOption = [
  {
    id: '0',
    value: 'sent',
    label: 'Sent',
  },
  {
    id: '1',
    value: 'scheduled',
    label: 'Scheduled',
  },
  {
    id: '2',
    value: 'draft',
    label: 'Drafts',
  },
];

export interface SocietyNewslettersTableProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  setCountMapping: React.Dispatch<React.SetStateAction<Record<string, number>>>;
}

export const SocietyNewslettersTable: React.FC<SocietyNewslettersTableProps> =
  memo(
    ({
      className = '', // custom class name
      style, // custom style
      setCountMapping,
    }: SocietyNewslettersTableProps) => {
      const contentClassNames = cn(
        {
          [styles.SocietyNewslettersTable]: true,
        },
        className
      );
      const router = useRouter();
      const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
      const scrollToBottom = () => {
        endOfPageRef.current?.scrollIntoView({ behavior: 'smooth' });
      };

      const { state } = router.query;

      useEffect(() => {
        if (state) {
          setSelectedNewsletterState(Number(state));
        }
      }, [state]);

      const { societyDashboard: dashboardData } = useSocietyProfile();

      const [touchpoint, setTouchpoint] = useState<{
        id: string;
        subject: string;
        state: string;
      } | null>(null);
      const [isDeleteButtonLoading, setIsDeleteButtonLoading] = useState(false);

      const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
        useState(false);
      const [confirmationScreenSelected, setConfirmationScreenSelected] =
        useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

      const {
        newslettersList,
        size,
        setSize,
        hasMoreRecords,
        totalCount,
        countMapping,
        selectedNewsletterState,
        setSelectedNewsletterState,
        isAPILoading,
        onDelete,
        isLoadingMore,
        copyToDraftNewsletter,
        updateNewsletter,
        mutateNewsletters,
      } = useNewsletters();

      setCountMapping(countMapping);

      const notificationInstance = useNotification();

      const deleteConfirmationScreenDesc = touchpoint ? (
        <div>
          <p>
            Are you sure you want to delete{' '}
            <span style={{ fontWeight: 700 }}>{touchpoint.subject}</span>{' '}
            newsletter?
          </p>
          <br />
          <p>All of the data and details associated with it will be gone.</p>
          <br />
        </div>
      ) : null;

      const sendNowConfirmationScreenDesc = touchpoint ? (
        <div>
          <p>Are you sure you want to send now the newsletter? </p>
        </div>
      ) : null;

      const cancelSendingConfirmationScreenDesc = touchpoint ? (
        <div>
          <p>Are you sure you want to cancel the sending?</p>
          <br />
          <p>
            If you cancel the sending, your newsletter will be moved to the
            Drafts section for future edits.
          </p>
          <br />
        </div>
      ) : null;

      const ConfirmationScreenProps = [
        {
          id: ConfirmationScreen.DELETE,
          title: 'Delete Newsletter',
          desc: deleteConfirmationScreenDesc,
          buttonContent: 'Yes, Delete',
          onCancel: () => {
            setIsConfirmationModalOpen(false);
            setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          },
          onConfirm: async () => {
            if (touchpoint) {
              setIsDeleteButtonLoading(true);
              if (touchpoint.state === 'sent') {
                const response = await updateNewsletter({
                  news_letter: { hidden: true },
                  id: touchpoint.id as string,
                });
                if (response) {
                  setIsConfirmationModalOpen(false);
                  setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
                  setTouchpoint(null);
                  mutateNewsletters();
                }
                setIsDeleteButtonLoading(false);
              } else {
                const response = await onDelete(touchpoint.id as string);
                if (response) {
                  setIsConfirmationModalOpen(false);
                  setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
                  setTouchpoint(null);
                }
                setIsDeleteButtonLoading(false);
              }
            }
          },
          isPrimary: false,
        },
        {
          id: ConfirmationScreen.SEND_NOW,
          title: 'Send now',
          desc: sendNowConfirmationScreenDesc,
          buttonContent: 'Yes, send now',
          onCancel: () => {
            setIsConfirmationModalOpen(false);
            setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          },
          onConfirm: async () => {
            if (touchpoint) {
              const response = await updateNewsletter({
                news_letter: { send_now: true },
                id: touchpoint.id as string,
              });
              if (response) {
                setIsConfirmationModalOpen(false);
                setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
                setTouchpoint(null);
                notificationInstance.success({
                  title: 'Newsletter sent successfully! 🚀',
                  message:
                    'Congratulations! Your newsletter has been successfully sent to your subscribers. 📬 ✨',
                });
                router.push(
                  `${PAGE_ROUTES.NEWSLETTERS}?state=${SocietyTabsType.SENT}`
                );
                mutateNewsletters();
              }
              setIsDeleteButtonLoading(false);
            }
          },
          isPrimary: true,
        },
        {
          id: ConfirmationScreen.CANCEL_SENDING,
          title: 'Cancel sending Newsletter',
          desc: cancelSendingConfirmationScreenDesc,
          buttonContent: 'Yes, cancel sending',
          onCancel: () => {
            setIsConfirmationModalOpen(false);
            setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          },
          onConfirm: async () => {
            if (touchpoint && touchpoint.state === 'scheduled') {
              const response = await updateNewsletter({
                news_letter: { state: 'draft', scheduled_at: null },
                id: touchpoint.id as string,
              });
              if (response) {
                setIsConfirmationModalOpen(false);
                setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
                setTouchpoint(null);
                mutateNewsletters();
              }
              setIsDeleteButtonLoading(false);
            }
          },
          isPrimary: false,
        },
      ];

      const getConfirmationModal = () => {
        const screen = ConfirmationScreenProps.find(
          (item) => item.id === confirmationScreenSelected
        );
        if (screen) {
          const { title, desc, buttonContent, onCancel, onConfirm, isPrimary } =
            screen;
          return (
            <Modal
              isOpen={isConfirmationModalOpen}
              onRequestClose={() => setIsConfirmationModalOpen(false)}
              isFullScreen={false}
            >
              <Confirmation
                title={title}
                desc={desc || ''}
                buttonContent={buttonContent}
                onCancel={onCancel}
                isLoading={isDeleteButtonLoading}
                onConfirm={onConfirm}
                isPrimary={isPrimary}
                secondaryActionOnRight={
                  title === 'Delete Newsletter' ? false : true
                }
              />
            </Modal>
          );
        }
      };

      const getActionMenu = (newsletter: NewsletterAttributes) => {
        const { id, subject, state } = newsletter;
        return [
          ...(['draft', 'scheduled'].includes(state)
            ? [
                {
                  id: '1',
                  label: 'Edit',
                  onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const pathname = PAGE_ROUTES.NEWSLETTER_EDIT.replace(
                      '[id]',
                      id
                    );
                    router.push(`${pathname}`);
                  },
                },
              ]
            : []),
          ...('scheduled' === state
            ? [
                {
                  id: '2',
                  label: 'Send now',
                  onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setTouchpoint({ id, subject, state });
                    setIsConfirmationModalOpen(true);
                    setConfirmationScreenSelected(ConfirmationScreen.SEND_NOW);
                  },
                },
                {
                  id: '3',
                  label: 'Cancel sending',
                  onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setTouchpoint({ id, subject, state });
                    setIsConfirmationModalOpen(true);
                    setConfirmationScreenSelected(
                      ConfirmationScreen.CANCEL_SENDING
                    );
                  },
                },
              ]
            : []),
          ...(['sent', 'scheduled'].includes(state)
            ? [
                {
                  id: '4',
                  label: 'Copy to drafts',
                  onClick: async (
                    event: React.MouseEvent<HTMLButtonElement>
                  ) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setTouchpoint({ id, subject, state });
                    const response = await copyToDraftNewsletter(id);
                    if (response) {
                      router.push(
                        PAGE_ROUTES.NEWSLETTER_EDIT.replace('[id]', response)
                      );
                    }
                  },
                },
              ]
            : []),
          {
            id: '5',
            label: 'Delete',
            onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              event.stopPropagation();
              setTouchpoint({ id, subject, state });
              setIsConfirmationModalOpen(true);
              setConfirmationScreenSelected(ConfirmationScreen.DELETE);
            },
          },
        ];
      };

      const newsletterData = useMemo(() => {
        if (!isAPILoading && !newslettersList.length) {
          return (
            <div className={styles.noResults}>
              <div className={styles.noResultsContent}>
                <Icon iconName={'icon_mailbox-with-letter'} size="auto" />
                <div className={styles.noResultsContentText}>
                  <p>Create your first newsletter</p>
                  <span>
                    Inform your audience about upcoming events and keep them
                    updated on the latest developments within your community.
                  </span>
                </div>
                <ButtonV3
                  onClick={() => router.push(PAGE_ROUTES.NEWSLETTER_CREATE)}
                  className={styles.createButton}
                >
                  Create new
                </ButtonV3>
              </div>
            </div>
          );
        } else if (!isAPILoading && newslettersList.length) {
          return (
            <table className={cn({ [styles.loading]: isLoadingMore })}>
              <thead>
                <tr>
                  {NewsletterColumns[selectedNewsletterState as number].map(
                    (column) => (
                      <th key={column.id} style={{ width: column.width }}>
                        {column.label}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {newslettersList.map((row) => {
                  const {
                    id,
                    subject,
                    scheduled_at,
                    created_at,
                    clicks,
                    opened,
                    sent_at,
                    state,
                    recipient_count,
                  } = row;

                  return (
                    <tr
                      key={id}
                      onClick={() => {
                        if (state !== 'sent') {
                          router.push(
                            PAGE_ROUTES.NEWSLETTER_EDIT.replace('[id]', id)
                          );
                        } else {
                          router.push(
                            PAGE_ROUTES.NEWSLETTER_PREVIEW + '/' + id
                          );
                        }
                      }}
                    >
                      <td>
                        <div className={cn(styles.title, styles.titleSection)}>
                          <p title={subject}>{subject}</p>
                        </div>
                      </td>
                      {selectedNewsletterState === SocietyTabsType.DRAFTS && (
                        <td>{format(new Date(created_at), 'MMM d, yyyy')}</td>
                      )}
                      {selectedNewsletterState ===
                        SocietyTabsType.SCHEDULED && (
                        <>
                          <td>{dashboardData?.subscribers_count || 0}</td>
                          <td>
                            {format(new Date(scheduled_at), 'MMM d, yyyy')}
                          </td>
                        </>
                      )}
                      {selectedNewsletterState === SocietyTabsType.SENT && (
                        <>
                          <td>
                            {sent_at &&
                              format(new Date(sent_at), 'MMM d, yyyy')}
                          </td>
                          <td>{recipient_count}</td>
                          <td>
                            {opened
                              ? ((opened / recipient_count) * 100).toFixed(2)
                              : 0}
                            %
                          </td>
                          <td>
                            {opened && clicks
                              ? ((clicks / opened) * 100).toFixed(2)
                              : 0}
                            %
                          </td>
                        </>
                      )}

                      <td>
                        <ActionMenu
                          options={getActionMenu(row)}
                          triggerButtonClassName={styles.actionmenu}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        }
      }, [selectedNewsletterState, newslettersList]);

      useEffect(() => {
        if (!isLoadingMore && size > 1) {
          scrollToBottom();
        }
      }, [isLoadingMore, size]);

      useEffect(() => {
        setSize(1);
        if (router.query && router.query.selectedEventState !== undefined) {
          const index = Number(router.query.selectedEventState);
          setSelectedNewsletterState(index);
        }

        return () => {
          endOfPageRef.current = null;
          setSelectedNewsletterState(SocietyTabsType.SENT);
        };
      }, []);

      return (
        <>
          <p className={styles.totalCount}>{totalCount} newsletters</p>
          <div
            className={contentClassNames}
            style={style}
            data-testid="SocietyNewslettersTable"
          >
            <Tab.Group
              selectedIndex={selectedNewsletterState}
              onChange={(index) => {
                router.push(
                  `${PAGE_ROUTES.NEWSLETTERS}?state=${index}`,
                  undefined,
                  { shallow: true }
                );
                setSelectedNewsletterState(index);
                setSize(1);
              }}
            >
              <Tab.List className={styles.tabList}>
                {EventStateOption.map((tab) => {
                  return (
                    <Tab
                      key={tab.id}
                      className={({ selected }) =>
                        cn(styles.tab, { [styles.tabSelected]: selected })
                      }
                    >
                      {tab.label}
                      {countMapping && countMapping[tab.value]
                        ? ` (${countMapping[tab.value]})`
                        : tab.label === 'Sent'
                        ? ` (0)`
                        : ''}
                    </Tab>
                  );
                })}
              </Tab.List>
              <Tab.Panels className={styles.tabPanels}>
                <Tab.Panel>{newsletterData}</Tab.Panel>
                <Tab.Panel>{newsletterData}</Tab.Panel>
                <Tab.Panel>{newsletterData}</Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
            {isLoadingMore ? (
              <Spinner size="small" className={styles.spinner} />
            ) : null}

            <Row align="center" className={styles.countsRow}>
              {newslettersList?.length ? (
                <p>
                  Showing {newslettersList?.length} of {totalCount}
                </p>
              ) : null}
              {hasMoreRecords ? (
                <Button
                  variant="text"
                  onClick={() => {
                    setSize(size + 1);
                  }}
                  className={styles.countsButton}
                >
                  Show more
                </Button>
              ) : null}
              <div ref={endOfPageRef}></div>
            </Row>
            {isConfirmationModalOpen ? getConfirmationModal() : null}
          </div>
        </>
      );
    }
  );

SocietyNewslettersTable.displayName = 'SocietyNewslettersTable';
